import {
  Outlet,
  createFileRoute,
  useLoaderData,
  useNavigate,
  useParams,
  useRouter,
} from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { DataTable } from '@/components/DataTable'
import { TaskDetailDrawer, taskColumns } from '@/components/Tasks'
import { getTask, listMyTasks } from '@/services/taskService'
import { getProject } from '@/services/clientService'
import { DataTableSkeleton } from '@/components/route-shell/skeletons/DataTableSkeleton'
import { isOverdue } from '@/lib/taskDisplay'
import { normalizeEntityId } from '@/lib/entityUrl'
import type { Task } from '@/types/task'
import '@/components/Tasks/Tasks.css'

interface MyTasksLoaderData {
  tasks: Task[]
  loadError: string | null
}

export const Route = createFileRoute('/_app/tasks')({
  head: () => ({ meta: [{ title: 'My Tasks · AERRIS' }] }),
  // Wave 1 of the page's data — the one discrete fetch (ADR 0027): the route holds its
  // skeleton until the desk is listed. A failure is part of the loader's DATA, not a throw:
  // the page's own error copy is approved UX, and the generic RouteError is for surprises.
  // Waves 2-3 (per-project names, the deep-linked stray Task) stay in the component.
  loader: async (): Promise<MyTasksLoaderData> => {
    try {
      return { tasks: await listMyTasks(), loadError: null }
    } catch {
      return { tasks: [], loadError: 'Your tasks could not be loaded. The server may be unreachable.' }
    }
  },
  pendingComponent: DataTableSkeleton,
  component: MyTasksPage,
})

/** Open work first, then what is late, then by urgency, then by how soon it is due. */
function byUrgency(a: Task, b: Task): number {
  const rank = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
  const openness = Number(isTerminal(a)) - Number(isTerminal(b))
  if (openness !== 0) return openness
  const late = Number(isOverdue(b)) - Number(isOverdue(a))
  if (late !== 0) return late
  const priority = rank[a.priority] - rank[b.priority]
  if (priority !== 0) return priority
  return (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31')
}

function isTerminal(task: Task): boolean {
  return task.status === 'DONE' || task.status === 'CANCELLED'
}

/**
 * **My Tasks** — the self-scoped Task dashboard: every Task the signed-in user creates, is assigned, or
 * reviews, across every Project they can access, grouped by Project (`aerris-client/CONTEXT.md`).
 *
 * No client/project filter applies here on purpose: this page answers "what is on my desk", and the
 * server decides what that includes by recomputing the accessible project set per request — so a
 * revocation removes a project's Tasks from this list on the next load, and a re-grant brings them back.
 *
 * It is also the target of every Task notification, whose link is `/tasks/{uuid}`
 * (`TaskNotifications`): the uuid arrives as this route's child param and opens that Task's drawer, even
 * when the Task is not in the list (a Reader following a mention, say). The list itself is rendered here
 * rather than in the child routes so following such a link does not tear the page down and rebuild it.
 */
function MyTasksPage() {
  const navigate = useNavigate()
  const router = useRouter()
  // Set when the /tasks/{uuid} child route matched — the notification deep link (vault.tsx precedent
  // for reading a child route's param from its layout). Normalised for the same reason as there: a
  // non-strict read from the parent layout may see the raw URL segment rather than a parsed value.
  const { taskUuid: rawTaskUuid } = useParams({ strict: false }) as { taskUuid?: string }
  const linkedTaskUuid = normalizeEntityId(rawTaskUuid) ?? rawTaskUuid
  // Loose on purpose, like `useParams` above: this component renders as the route's OWN component,
  // so the nearest match is always this route and its loader data — strict ids would break the
  // component-test harness, which mounts it at a bare path.
  const loaded = useLoaderData({ strict: false }) as MyTasksLoaderData
  // Loader seeds, component owns (ADR 0027): the drawer edits this list in place (`onChanged`),
  // so it is state — re-seeded whenever the loader re-runs (close-drawer invalidation, below).
  const [tasks, setTasks] = useState<Task[]>(loaded.tasks)
  const [projectNames, setProjectNames] = useState<Record<string, string>>({})
  const [loadError, setLoadError] = useState<string | null>(loaded.loadError)
  /** The row the reader clicked; the URL wins over it when a notification named a Task. */
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  /** A deep-linked Task that is not in the list — fetched on its own so the link still lands. */
  const [fetched, setFetched] = useState<Task | null>(null)

  useEffect(() => {
    setTasks(loaded.tasks)
    setLoadError(loaded.loadError)
  }, [loaded])

  // Which Task the drawer is showing: the URL when a notification named one, else the clicked row. The
  // open Task is derived rather than copied into state, so a reload of the list flows straight into the
  // drawer instead of leaving a stale image behind it.
  const activeUuid = linkedTaskUuid ?? selectedUuid
  const openTask =
    tasks.find((task) => task.uuid === activeUuid) ??
    (fetched && fetched.uuid === activeUuid ? fetched : null)

  // A deep link must land on the Task it names even when the list has no such row — a Reader following a
  // mention on a Task they neither own nor review.
  useEffect(() => {
    if (!activeUuid || tasks.some((task) => task.uuid === activeUuid)) return
    if (fetched?.uuid === activeUuid) return
    let cancelled = false
    getTask(activeUuid)
      .then((task) => {
        if (!cancelled) setFetched(task)
      })
      .catch(() => {
        // Gone, or not ours to see: leave the list on screen rather than an error the reader can't act on.
      })
    return () => {
      cancelled = true
    }
  }, [activeUuid, tasks, fetched?.uuid])

  // Project names one lookup at a time: the tasks arrive project-scoped but unnamed, and the
  // client/project context loads projects lazily per client, so it cannot be relied on across projects.
  useEffect(() => {
    const unknown = [...new Set(tasks.map((t) => t.projectUuid))].filter((uuid) => !projectNames[uuid])
    if (unknown.length === 0) return
    let cancelled = false
    Promise.all(
      unknown.map((uuid) =>
        getProject(uuid)
          .then((project) => [uuid, project.name] as const)
          .catch(() => [uuid, 'Project'] as const),
      ),
    ).then((pairs) => {
      if (!cancelled) setProjectNames((prev) => ({ ...prev, ...Object.fromEntries(pairs) }))
    })
    return () => {
      cancelled = true
    }
  }, [tasks, projectNames])

  const groups = useMemo(() => {
    const byProject = new Map<string, Task[]>()
    for (const task of tasks) {
      const group = byProject.get(task.projectUuid)
      if (group) group.push(task)
      else byProject.set(task.projectUuid, [task])
    }
    return [...byProject.entries()]
      .map(([projectUuid, group]) => ({
        projectUuid,
        name: projectNames[projectUuid] ?? 'Loading project…',
        tasks: [...group].sort(byUrgency),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [tasks, projectNames])

  const columns = useMemo(() => taskColumns(), [])

  const openCount = tasks.filter((task) => !isTerminal(task)).length

  const closeDrawer = () => {
    setSelectedUuid(null)
    setFetched(null)
    // Drop the deep-link segment so the drawer doesn't reopen, and re-read: the drawer can change
    // status, assignee and fields, and the list must show the server's truth rather than ours.
    // The re-read is the loader's job now — invalidate-on-mutation (ADR 0027), which re-runs it
    // and flows back in through the seed effect above.
    if (linkedTaskUuid) navigate({ to: '/tasks', search: (prev) => prev })
    void router.invalidate()
  }

  return (
    <div className="task-page">
      <header className="task-page-header">
        <h1>My Tasks</h1>
        <span className="task-page-subtitle">
          {`${openCount} open of ${tasks.length} across ${groups.length} ${groups.length === 1 ? 'project' : 'projects'}`}
        </span>
      </header>

      {loadError && <div className="task-error-state">{loadError}</div>}

      {groups.length === 0 ? (
        <div className="task-empty-state">
          Nothing on your desk. Tasks you create, are assigned, or review appear here.
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.projectUuid} className="task-project-group">
            <h2 className="task-project-group-title">{group.name}</h2>
            <DataTable
              columns={columns}
              data={group.tasks}
              getRowId={(task) => task.uuid}
              onRowClick={(task) => setSelectedUuid(task.uuid)}
              showToolbar={false}
              compact
              emptyMessage="No tasks in this project."
            />
          </section>
        ))
      )}

      <TaskDetailDrawer
        task={openTask}
        onClose={closeDrawer}
        onChanged={(updated) => {
          setTasks((prev) => prev.map((t) => (t.uuid === updated.uuid ? updated : t)))
          setFetched((prev) => (prev && prev.uuid === updated.uuid ? updated : prev))
        }}
      />

      {/* The child routes render nothing: /tasks/{uuid} exists to carry a notification's target. */}
      <Outlet />
    </div>
  )
}
